import { useCallback, useEffect, useRef, useState } from "react";
import { lostSalesService } from "../../utils/services/lost-sales.service";

// Date inputs fire on every valid keystroke-date; wait for the user to settle.
const PARAM_DEBOUNCE_MS = 400;
// The eligibility query scans a year of orders (seconds on large warehouses),
// so switching back to a recent warehouse/period reuses the last answer.
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_MAX_ENTRIES = 8;
const cache = new Map();

const readCache = (key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.at > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.rows;
};

const writeCache = (key, rows) => {
  cache.delete(key);
  cache.set(key, { rows, at: Date.now() });
  while (cache.size > CACHE_MAX_ENTRIES) {
    cache.delete(cache.keys().next().value);
  }
};

/** Test hook: module-level cache would otherwise leak between tests. */
export const clearLostSalesEligibilityCache = () => cache.clear();

/**
 * Warehouse-eligible population (inventory AND sales in the period) for the
 * "All SKUs" / "All Products" mode: its count before the run, and the rows the
 * run analyzes. Only enabled while that mode is on — the query scans a year of
 * orders, so it never runs for manual selection.
 *
 * Stale responses are dropped by request sequence, so quickly changing the
 * warehouse or dates can never show the previous selection's population.
 */
const useLostSalesEligibility = ({
  clientId,
  warehouseId,
  startDate,
  endDate,
  enabled = true,
}) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retryToken, setRetryToken] = useState(0);
  const requestSeq = useRef(0);

  const validParams =
    enabled &&
    Boolean(clientId && warehouseId && startDate && endDate) &&
    startDate <= endDate;

  useEffect(() => {
    const seq = ++requestSeq.current;
    if (!validParams) {
      setRows(null);
      setLoading(false);
      setError("");
      return undefined;
    }
    const cacheKey = [clientId, warehouseId, startDate, endDate].join("|");
    // Only successful answers are cached, so a retry after an error refetches.
    const cached = readCache(cacheKey);
    if (cached) {
      setRows(cached);
      setLoading(false);
      setError("");
      return undefined;
    }
    // Never show the previous warehouse/period's population while loading.
    setRows(null);
    setLoading(true);
    setError("");
    const handle = setTimeout(async () => {
      try {
        const data = await lostSalesService.eligibleProducts(
          clientId,
          warehouseId,
          startDate,
          endDate,
        );
        if (seq !== requestSeq.current) return;
        const nextRows = Array.isArray(data?.eligible_products)
          ? data.eligible_products
          : [];
        writeCache(cacheKey, nextRows);
        setRows(nextRows);
      } catch (requestError) {
        if (seq !== requestSeq.current) return;
        setRows(null);
        setError(
          requestError?.response?.data?.message ||
            "Couldn't check which SKUs have data for this warehouse and period.",
        );
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }, PARAM_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [validParams, clientId, warehouseId, startDate, endDate, retryToken]);

  const retry = useCallback(() => setRetryToken((token) => token + 1), []);

  // `rows` always belong to the CURRENT params (null while loading), so the
  // run reuses them instead of refetching.
  return { rows, loading, error, retry };
};

export default useLostSalesEligibility;
